import type { Dispatch } from 'react';
import { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import * as Sentry from '@sentry/react';

import { ROUTES } from '@constants/routes';
import type {
  FamilyHistory,
  PatientOrderingProduct,
  PatientOrderingSubmitOrderRequest,
} from '@customTypes/test-ordering';
import useToast from '@hooks/useToast';
import { fetchUserTestRequests } from '@store/actions';
import { submitRerequisition } from '@store/actions/patientPortal';
import { submitOrder } from '@store/actions/test-ordering';
import { Context } from '@store/store';
import { isMinorDOB } from '@utils/dates';

import { OrderActions, type OrderActionType } from '../types/Order.actions';
import type { GuardianPayload, SubjectPayload } from '../types/Order.types';
import type { Consent, FormType } from '../types/Wizard.types';
import { clearWizardState } from '../utils/orderStorage';

export interface UseWizardSubmissionProps {
  values: FormType;
  campaign_code?: string;
  subject: SubjectPayload;
  guardian: GuardianPayload | null;
  family_history: FamilyHistory;
  products: PatientOrderingProduct[];
  allConsents: Consent[];
  dnaVisitConsent: Consent | null;
  hieConsent: Consent | null;
  researchConsent: Consent | null;
  transactionalSmsConsent: Consent | null;
  marketingConsent: Consent | null;
  minorConsent: Consent | null;
  dispatch: Dispatch<OrderActionType>;
  partner: string | undefined;
  isCurrentStepComplete: boolean;
  setShowErrors: (show: boolean) => void;
  flushStorage: () => void;
  isRerequisition?: boolean;
}

interface UseWizardSubmissionReturn {
  handleSubmitOrderClick: () => Promise<void>;
  submitting: boolean;
}

/**
 * Hook that handles order submission to the API.
 * Builds payload, handles consents, and navigates to success page.
 */
export function useWizardSubmission({
  values,
  campaign_code,
  subject,
  guardian,
  family_history,
  products,
  allConsents,
  dnaVisitConsent,
  hieConsent,
  researchConsent,
  transactionalSmsConsent,
  marketingConsent,
  minorConsent,
  dispatch,
  partner,
  isCurrentStepComplete,
  setShowErrors,
  flushStorage,
  isRerequisition = false,
}: UseWizardSubmissionProps): UseWizardSubmissionReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const storeContext = useContext(Context);
  const storeDispatch = Array.isArray(storeContext) ? storeContext[1] : undefined;

  const handleSubmitOrderClick = async () => {
    if (!isCurrentStepComplete) {
      setShowErrors(true);
      return;
    }

    const hasSelectedProduct = (products || []).some(product => product.product_id);
    if (!hasSelectedProduct) return;

    setSubmitting(true);

    try {
      // Build consent URIs based on user selections
      const consentsSelected: string[] = [];
      if (values.consents?.acceptMinor && minorConsent?.consent_uri) {
        consentsSelected.push(minorConsent.consent_uri);
      }
      if (values.consents?.acceptDNAVisit && dnaVisitConsent?.consent_uri) {
        consentsSelected.push(dnaVisitConsent.consent_uri);
      }
      if (values.consents?.acceptGeneral && allConsents.length) {
        consentsSelected.push(...allConsents.map(c => c.consent_uri).filter(Boolean));
      }
      if (values.consents?.acceptHIE && hieConsent?.consent_uri) {
        consentsSelected.push(hieConsent.consent_uri);
      }
      if (values.consents?.acceptResearchContact && researchConsent?.consent_uri) {
        consentsSelected.push(researchConsent.consent_uri);
      }
      if (values.consents?.acceptTransactionalSms && transactionalSmsConsent?.consent_uri) {
        consentsSelected.push(transactionalSmsConsent.consent_uri);
      }
      if (values.consents?.acceptMarketing && marketingConsent?.consent_uri) {
        consentsSelected.push(marketingConsent.consent_uri);
      }

      // Persist consents into context (payload-aligned)
      dispatch({ type: OrderActions.SET_CONSENTS_GRANTED, value: consentsSelected });

      // Derive product_ids from products in order
      const product_ids = (products || []).map(p => p.product_id || '').filter(productId => productId !== '');

      const isMinorSubject = isMinorDOB(subject.demographics.dob);

      const payload: PatientOrderingSubmitOrderRequest = {
        campaign_code: campaign_code || null,
        consents_granted: consentsSelected,
        family_history,
        payment_intent_id: values.paymentIntentId || null,
        product_ids,
        promo_code: values.promoCode || null,
        shipping_address: {
          street_address: values.address?.street || '',
          street_address_2: values.address?.street2 || '',
          city: values.address?.city || '',
          region: values.address?.state || '',
          postal_code: values.address?.zip || '',
        },
        subject: {
          first_name: subject.name.first || '',
          last_name: subject.name.last || '',
          dob: subject.demographics.dob || '',
          sex: subject.demographics.sex || '',
          email: subject.contact_info[0]?.email || null,
          phone: subject.contact_info[0]?.phone || null,
        },
      };

      if (isMinorSubject && guardian) {
        payload.guardian = {
          first_name: guardian.first_name,
          last_name: guardian.last_name,
          dob: guardian.dob,
          email: guardian.email,
          phone: guardian.phone,
          relationship: guardian.relationship,
        };
      }

      // Flush any pending storage writes before clearing
      flushStorage();

      if (isRerequisition) {
        await submitRerequisition({
          consents_granted: consentsSelected,
          family_history,
          individual: searchParams.get('individual') || undefined,
          payment_intent_id: values.paymentIntentId || null,
          product_ids,
        });
      } else {
        await submitOrder(payload);
      }

      try {
        const requests = await fetchUserTestRequests(true);
        storeDispatch?.({ type: 'SET_ALL_TEST_REQUESTS', payload: requests || [] });
      } catch {
        // My Orders refetches on visit if this fails.
      }

      // Clear persisted draft after successful submit
      try {
        sessionStorage.removeItem('partnerOrder.state');
        clearWizardState();
        sessionStorage.removeItem('po:wizardDraft');
        sessionStorage.removeItem('po:wizardStep');
      } catch {
        // ignore
      }
      // Navigate to whats-next page (absolute path)
      const whatsNextUrl = partner
        ? `/${ROUTES.PARTNERSHIP}/${partner}/${ROUTES.ORDER_WHATS_NEXT}`
        : `${ROUTES.ORDER}/${ROUTES.ORDER_WHATS_NEXT}`;
      if (isRerequisition) {
        navigate(whatsNextUrl, { state: { isRerequisition: true } });
      } else {
        navigate(whatsNextUrl);
      }
    } catch (error) {
      // Log error to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'useWizardSubmission',
          action: isRerequisition ? 'submitRerequisition' : 'submitOrder',
        },
        extra: {
          campaign_code,
          partner,
          hasPaymentIntent: !!values.paymentIntentId,
          productCount: products?.length || 0,
        },
      });

      // Show user-friendly error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : 'Failed to submit order. Please try again.';

      addToast({
        variant: 'danger',
        headerTitle: 'Order Submission Failed',
        content:
          errorMessage ||
          'An error occurred while submitting your order. Please try again or contact support if the problem persists.',
        autohide: false,
      });

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to submit partner order:', error);
      }

      // Note: We intentionally do NOT clear storage or reset state on failure
      // This allows the user to retry without losing their progress
    } finally {
      setSubmitting(false);
    }
  };

  return {
    handleSubmitOrderClick,
    submitting,
  };
}
